import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  MessageCircle, PackageCheck, Truck, XCircle, Clock, Loader2,
  AlertTriangle, PhoneCall, Building2, CalendarClock, CheckCircle2,
  Package, User, Hash, DollarSign,
} from "lucide-react";

// URL do backend (mesmo host da API — o endpoint é público)
const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function fmtBRL(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtPhone(t) {
  const d = String(t || "").replace(/\D/g, "");
  if (d.length === 13 && d.startsWith("55")) return `+55 (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  return t;
}

// fev/2026: consolida todos os números de pedido da entrega (deduplicados).
// Cobre 3 modos: pedido único, Receita+Revenda, e pedidos_multiplos (array).
// pedidos_multiplos pode vir como lista de strings OU objetos {numero}.
function getAllPedidos(entrega) {
  if (!entrega) return [];
  const seen = new Set();
  const out = [];
  const push = (v) => {
    const s = String(v ?? "").trim();
    if (s && !seen.has(s)) { seen.add(s); out.push(s); }
  };
  push(entrega.numero_pedido);
  push(entrega.numero_pedido_receita);
  push(entrega.numero_pedido_revenda);
  (entrega.pedidos_multiplos || []).forEach((p) => {
    if (typeof p === "string" || typeof p === "number") push(p);
    else if (p && typeof p === "object") push(p.numero || p.numero_pedido);
  });
  return out;
}

// Deriva um timeline linear do pedido a partir dos timestamps da entrega.
// Cada item: {label, at, current}. O último item é o passo atual.
// Fluxo: Pedido recebido → Em conferência → Saiu para entrega → Entregue.
// Se pulou algum passo (ex: cadastrou direto como em_transito), usa fallback
// com `updated_at` pra não deixar buracos.
function buildHistorico(entrega, status) {
  const items = [];
  if (entrega.created_at) items.push({ key: "criado", label: "Pedido recebido", at: entrega.created_at });

  const terminal = status === "concluida" || status === "cancelada" ||
                    status === "remarcar" || status === "remarcada";

  // "Em conferência com entregador" aparece quando o pedido passou por esse estado
  // (aguardando_conferencia_at existe) OU o status atual é aguardando_conferencia.
  const jaConferiu = !!entrega.aguardando_conferencia_at;
  if (jaConferiu || status === "aguardando_conferencia") {
    items.push({
      key: "conferencia",
      label: "Em conferência com entregador",
      at: entrega.aguardando_conferencia_at,
    });
  }

  // "Saiu para entrega" só quando o pedido efetivamente passou de em_transito
  // em diante — evita mostrar hr_saida órfão quando o status voltou pra
  // aguardando_conferencia por algum motivo administrativo.
  const jaSaiu = entrega.hr_saida && (
    status === "em_transito" || status === "concluida" ||
    status === "cancelada" || status === "remarcar" || status === "remarcada"
  );
  if (jaSaiu) items.push({ key: "saiu", label: "Saiu para entrega", at: entrega.hr_saida });
  if (status === "concluida" && entrega.hr_chegada) {
    items.push({ key: "entregue", label: "Entregue ao cliente", at: entrega.hr_chegada });
  }
  if (status === "cancelada") items.push({ key: "cancelada", label: "Entrega cancelada", at: entrega.updated_at });
  if (status === "remarcar" || status === "remarcada") {
    items.push({ key: "remarcada", label: "Entrega remarcada", at: entrega.updated_at });
  }
  // Marca o último como current (é o passo atual/mais recente).
  // Estados terminais também marcam apenas o último — os anteriores ficam cinza.
  return items.map((it, i) => ({ ...it, current: i === items.length - 1 }));
}

export default function TrackingPublic() {
  const { hash } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/rastreio/${hash}`);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Refresh a cada 20s enquanto em_transito
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [hash]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3 text-slate-500" data-testid="tracking-loading">
          <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
          <span>Carregando informações do pedido…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="text-center max-w-md" data-testid="tracking-error">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-slate-800">Não foi possível carregar</h1>
          <p className="text-sm text-slate-500 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  const status = data?.status;

  if (status === "not_found") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="text-center max-w-md" data-testid="tracking-not-found">
          <XCircle className="w-14 h-14 text-rose-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-800">Link não encontrado</h1>
          <p className="text-sm text-slate-500 mt-2">
            Este link de rastreamento não é válido ou já foi removido.
          </p>
        </div>
      </div>
    );
  }

  if (status === "expired") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="text-center max-w-md" data-testid="tracking-expired">
          <Clock className="w-14 h-14 text-slate-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-800">Este link expirou</h1>
          <p className="text-sm text-slate-500 mt-2">
            Links de rastreamento ficam ativos por 24 horas. Entre em contato com a farmácia se precisar de outra informação.
          </p>
        </div>
      </div>
    );
  }

  const empresa = data?.empresa || {};
  const entrega = data?.entrega || {};
  const entregador = data?.entregador;
  // fev/2026 (iter 75): mapa removido — não usamos mais `data.position` aqui.
  // O backend continua enviando quando GPS ativo caso o mapa seja reativado.
  const cor = empresa.cor_primaria || "#8b5cf6";

  // Header comum (empresa branding)
  const Header = (
    <div
      className="w-full py-4 px-5 flex items-center gap-3 shadow-sm"
      style={{ background: cor, color: "#fff" }}
      data-testid="tracking-header"
    >
      {empresa.logo_base64 ? (
        <img src={empresa.logo_base64} alt="logo" className="w-11 h-11 rounded-full bg-white object-contain p-1" />
      ) : (
        <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center">
          <Building2 className="w-6 h-6 text-white" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-bold text-base leading-tight truncate" data-testid="tracking-empresa-nome">
          {empresa.nome || "Farmácia"}
        </div>
        <div className="text-xs text-white/85">Rastreamento do pedido</div>
      </div>
    </div>
  );

  // Status concluída — tela de agradecimento
  if (status === "concluida") {
    const pedidos = getAllPedidos(entrega);
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        {Header}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-md" data-testid="tracking-concluida">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                 style={{ background: `${cor}15` }}>
              <PackageCheck className="w-10 h-10" style={{ color: cor }} />
            </div>
            <h1 className="text-2xl font-extrabold text-slate-800">Obrigado!</h1>
            <p className="text-slate-600 mt-2">
              Seu pedido foi entregue{entrega.hr_chegada ? ` às ${fmtTime(entrega.hr_chegada)}` : ""}.
            </p>
            {pedidos.length > 0 && (
              <div className="mt-4 inline-block bg-white border rounded-lg px-4 py-2 text-sm text-slate-600"
                   data-testid="tracking-pedidos-badge">
                {pedidos.length === 1 ? "Pedido " : "Pedidos "}
                <b>{pedidos.map((n) => `#${n}`).join(", ")}</b>
              </div>
            )}
            {empresa.whatsapp_farmacia && (
              <a
                href={`https://wa.me/${(empresa.whatsapp_farmacia || "").replace(/\D/g, "")}`}
                target="_blank" rel="noreferrer"
                className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold text-sm"
                style={{ background: "#25d366" }}
                data-testid="tracking-whatsapp-cta"
              >
                <MessageCircle className="w-4 h-4" />
                Falar com a farmácia
              </a>
            )}
          </div>
        </div>
        <FooterExpira expires={data.expires_at} />
      </div>
    );
  }

  // Status cancelada / remarcada — encerra o link com contato da empresa
  // (fev/2026: internamente o backend salva "remarcar", mas exibimos "remarcada")
  if (status === "cancelada" || status === "remarcar" || status === "remarcada") {
    const isCancelada = status === "cancelada";
    const nomeEmpresa = empresa.nome || "a farmácia";
    const Icon = isCancelada ? XCircle : CalendarClock;
    const iconColor = isCancelada ? "text-rose-500" : "text-amber-500";
    const bgTone = isCancelada ? "bg-rose-50" : "bg-amber-50";
    const titulo = isCancelada ? "Entrega cancelada" : "Entrega remarcada";
    const mensagem = isCancelada
      ? `Esta entrega foi cancelada. Entre em contato com a ${nomeEmpresa} para mais informações.`
      : `Esta entrega foi remarcada. Entre em contato com a ${nomeEmpresa} para mais informações.`;
    const testId = isCancelada ? "tracking-cancelada" : "tracking-remarcada";
    const pedidos = getAllPedidos(entrega);

    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        {Header}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-md" data-testid={testId}>
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${bgTone}`}>
              <Icon className={`w-10 h-10 ${iconColor}`} />
            </div>
            <h1 className="text-2xl font-extrabold text-slate-800">{titulo}</h1>
            <p className="text-slate-600 mt-2">{mensagem}</p>
            {pedidos.length > 0 && (
              <div className="mt-4 inline-block bg-white border rounded-lg px-4 py-2 text-sm text-slate-600"
                   data-testid="tracking-pedidos-badge">
                {pedidos.length === 1 ? "Pedido " : "Pedidos "}
                <b>{pedidos.map((n) => `#${n}`).join(", ")}</b>
              </div>
            )}
            {empresa.whatsapp_farmacia && (
              <a
                href={`https://wa.me/${(empresa.whatsapp_farmacia || "").replace(/\D/g, "")}`}
                target="_blank" rel="noreferrer"
                className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold text-sm"
                style={{ background: "#25d366" }}
                data-testid="tracking-whatsapp-cta"
              >
                <MessageCircle className="w-4 h-4" />
                Falar com a farmácia
              </a>
            )}
          </div>
        </div>
        <FooterExpira expires={data.expires_at} />
      </div>
    );
  }

  // Status em curso (em_transito, aguardando_conferencia, aguardando) — SEM MAPA.
  // Mostra apenas o status atual + histórico + dados do pedido + contato.
  // O mapa foi removido intencionalmente (fev/2026, iter 75) e só será
  // reativado quando o usuário instruir explicitamente.
  const historico = buildHistorico(entrega, status);
  const pedidos = getAllPedidos(entrega);
  const statusLabel = status === "em_transito"
    ? "A caminho"
    : status === "aguardando_conferencia"
      ? "Entregador conferindo pedido"
      : "Preparando seu pedido";
  const statusSub = status === "em_transito"
    ? "Seu pedido saiu para entrega"
    : status === "aguardando_conferencia"
      ? "O entregador está conferindo os itens antes de sair"
      : "Nossa equipe está separando seus itens";

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="tracking-em-transito">
      {Header}
      <div className="flex-1 flex flex-col">
        {/* Card do status atual — grande, em destaque */}
        <div className="px-5 py-6 bg-white border-b">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
                 style={{ background: `${cor}15` }}>
              <Truck className="w-7 h-7" style={{ color: cor }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-lg font-extrabold text-slate-800 leading-tight" data-testid="tracking-status-label">
                {statusLabel}
              </div>
              <div className="text-sm text-slate-500 mt-1" data-testid="tracking-status-line">
                {statusSub}
              </div>
              {entrega.hr_saida && status === "em_transito" && (
                <div className="text-xs text-slate-400 mt-1">
                  Saiu para entrega às {fmtTime(entrega.hr_saida)}
                </div>
              )}
              {data.eta?.eta_min != null && status === "em_transito" && (
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold" style={{ background: `${cor}15`, color: cor }} data-testid="tracking-eta">
                  <Clock className="w-4 h-4" />Previsão de chegada: ~{data.eta.eta_min} min
                  <span className="text-[10px] font-normal opacity-70">({data.eta.source === "gps" ? "posição do entregador" : "estimativa"})</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Histórico de status */}
        {historico.length > 0 && (
          <div className="bg-white border-b px-5 py-4" data-testid="tracking-historico">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
              Histórico do pedido
            </div>
            <ol className="space-y-3">
              {historico.map((it) => (
                <li key={it.key} className="flex items-start gap-3">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                      it.current ? "" : "bg-slate-200"
                    }`}
                    style={it.current ? { background: cor } : undefined}
                    data-testid={`tracking-hist-${it.key}`}
                  >
                    <CheckCircle2 className={`w-4 h-4 ${it.current ? "text-white" : "text-slate-500"}`} />
                  </div>
                  <div className="flex-1">
                    <div className={`text-sm ${it.current ? "font-bold text-slate-800" : "text-slate-600"}`}>
                      {it.label}
                    </div>
                    {it.at && (
                      <div className="text-xs text-slate-400">{fmtDateTime(it.at)}</div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Detalhes do pedido */}
        <div className="bg-white border-b px-5 py-4">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
            Detalhes do pedido
          </div>
          <div className="space-y-3 text-sm">
            {pedidos.length === 1 && (
              <div className="flex items-center gap-3" data-testid="tracking-detail-pedido">
                <Hash className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="text-slate-500 flex-1">Nº do pedido</span>
                <span className="font-mono font-bold text-slate-800">#{pedidos[0]}</span>
              </div>
            )}
            {pedidos.length > 1 && (
              <div className="flex items-start gap-3" data-testid="tracking-detail-pedidos">
                <Hash className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                <span className="text-slate-500 shrink-0">Nº dos pedidos</span>
                <div className="flex-1 flex flex-wrap gap-1.5 justify-end">
                  {pedidos.map((n) => (
                    <span
                      key={n}
                      className="font-mono font-bold text-slate-800 bg-slate-100 rounded-md px-2 py-0.5 text-xs"
                      data-testid={`tracking-detail-pedido-item-${n}`}
                    >
                      #{n}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {entrega.cliente_nome && (
              <div className="flex items-center gap-3" data-testid="tracking-detail-cliente">
                <User className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="text-slate-500 flex-1">Cliente</span>
                <span className="font-semibold text-slate-800 truncate">{entrega.cliente_nome}</span>
              </div>
            )}
            {fmtBRL(entrega.valor) && (
              <div className="flex items-center gap-3" data-testid="tracking-detail-valor">
                <DollarSign className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="text-slate-500 flex-1">Valor do pedido</span>
                <span className="font-bold text-slate-800">{fmtBRL(entrega.valor)}</span>
              </div>
            )}
            {entregador?.nome && (
              <div className="flex items-center gap-3" data-testid="tracking-detail-entregador">
                <Package className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="text-slate-500 flex-1">Entregador</span>
                <span className="font-semibold text-slate-800">{entregador.nome}</span>
              </div>
            )}
          </div>
        </div>

        {/* Botão de contato */}
        {empresa.whatsapp_farmacia && (
          <div className="px-5 py-4 bg-white">
            <a
              href={`https://wa.me/${(empresa.whatsapp_farmacia || "").replace(/\D/g, "")}`}
              target="_blank" rel="noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-white font-semibold text-sm w-full"
              style={{ background: "#25d366" }}
              data-testid="tracking-whatsapp-cta"
            >
              <PhoneCall className="w-4 h-4" />
              Falar com a farmácia ({fmtPhone(empresa.whatsapp_farmacia)})
            </a>
          </div>
        )}
      </div>
      <FooterExpira expires={data.expires_at} />
    </div>
  );
}

function FooterExpira({ expires }) {
  if (!expires) return null;
  return (
    <div className="text-center text-xs text-slate-400 py-3 bg-slate-50 border-t"
         data-testid="tracking-footer">
      Link válido até {fmtDateTime(expires)} · Atualiza automaticamente
    </div>
  );
}
